import React, { useMemo, useState, type JSX } from 'react';
import {Box, Button, Collapse, Grid, IconButton, Link, Menu, MenuItem, Typography} from '@mui/material';
import {Add, MoreHoriz, Remove} from '@mui/icons-material';
import {styled, Theme} from '@mui/material/styles';
import {formatLink} from "../../../util/Formatter";

export interface IActionItem {
    icon?: JSX.Element;
    label: string;
    onClick: (param: IData) => void;
    id?: string | number;
}

export interface IData {
    [key: string]: string | number | boolean;
}

interface IDataValue {
    id?: string | number;
    value: string | number | boolean;
    label: string;
    link?: { baseUrl: string, queryParam?: string };
    onClick?: (param: IData) => void;
}

/**
 * @deprecated
 */
export interface ITransformedData {
    [key: string]: IDataValue;
}

export interface IInfoCard {
    /** an object with keys that correlate to the provided head cells*/
    data: IData;
    /** an array of id/labels that correlate to the data*/
    headCells: Array<{ id: string; label: string }>;
    /** if true, a hot dog menu will render*/
    hasAction?: boolean;
    /** an array of actions for the card. each takes an icon, a label, and an onClick handler.*/
    actionItems?: IActionItem[];
    /** identifies which data, if any, should be rendered as a link */
    links?: { [key: string]: { baseUrl: string, queryParam?: string } };
    /** action items that are rendered based on the data id (passed as 'inx' prop)*/
    conditionalActionItems?: IActionItem[];
    /** applied to the top-level grid component*/
    rootClassName?: string;
    /** applied to the collapsible component*/
    subRowClassName?: string;
    /** applied to all labels*/
    labelClassName?: string;
    /** applied to the all values*/
    valueClassName?: string;
    /** the id of the item that conditionalActionItems are renderd for*/
    inx?: string | number;
    /** true = collapsed, false = expanded*/
    collapse: boolean,
    /** handles to expand/collapse function*/
    handleToggle: (id: string) => void,
    /** optional buttons to display on the first row */
    visibleButton?: IActionItem
}

const MainContainerItem = styled(Grid)(({ theme }: { theme: Theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: '0px 1px 3px 0px #0000001F',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  overflow: 'auto',
  wordWrap: 'break-word',
  [theme.breakpoints.up('sm')]: {
    overflow: 'hidden',
  },
  position: 'relative',
}));

const InfoLabel = styled(Typography)(() => ({
  fontFamily: 'Inter',
  fontWeight: 400,
  color: '#00000061',
  textTransform: 'uppercase',
  whiteSpace: 'normal',
}));

const InfoValue = styled(Typography)(() => ({
  fontFamily: 'Inter',
  fontWeight: 500,
  whiteSpace: 'normal',
}));

const InfoValueLink = styled(Link)(() => ({
  fontFamily: 'Inter',
  fontWeight: 500,
  whiteSpace: 'normal',
}));

const InfoValueData = styled(Collapse)(({ theme }: { theme: Theme }) => ({
  backgroundColor: '#fff',
  marginTop: theme.spacing(1),
  borderRadius: theme.spacing(1),
  minWidth: '100%',
  overflowX: 'auto',
}));

const MenuContainer = styled(Menu)(() => ( {
  '& .MuiPopover-paper': {
    boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
  },
}));

const MarginLeftTypography = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginLeft: theme.spacing(2),
}));

const Header = styled(Grid)(() => ({
  position: 'relative',
  padding: "0em 1em",
  display: 'flex',
  alignItems: 'center',
}));

const HeaderAction = styled(Grid)(({ theme }: { theme: Theme }) => ({
  position: 'absolute',
  right: ".2em",
  alignItems: 'center',
  justifyContent: 'center',
  top: "4%",
  [theme.breakpoints.up('sm')]: {
    top: "20%",
  }
}));

const HeaderActionSub = styled(Grid)(() => ({
  position: 'absolute',
  right: "0em",
  top: "0em",
  height: "100%",
  width: "auto",
}));

const SubRowHeader = styled(Grid)(() => ({
  position: 'relative',
  flexWrap: 'wrap',
  padding: "1em",
}));

/**
 * @deprecated
 * @param key
 */
export const transformKey = (key: string) => {
    return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
};

/**
 * Old InfoCard
 * @param data
 * @param actionItems
 * @param links
 * @param headCells
 * @param conditionalActionItems
 * @param rootClassName
 * @param subRowClassName
 * @param labelClassName
 * @param valueClassName
 * @param inx
 * @param hasAction
 * @param collapse
 * @param handleToggle
 * @param visibleButton
 * @constructor
 * @deprecated
 */
const InfoCard: React.FC<IInfoCard> = ({
    data,
    actionItems,
    links,
    headCells,
    conditionalActionItems,
    rootClassName,
    subRowClassName,
    labelClassName,
    valueClassName,
    inx,
    hasAction = true,
    collapse,
    handleToggle,
    visibleButton
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
        null
    );
    const subRowData = Object.entries(data).slice(6);

    const topRowLength = visibleButton ? 5 : 6;

    const transformedUpperSectionData = useMemo<ITransformedData>(() => {
        const upperSectionDataKeys = headCells.slice(0, topRowLength).map(cell => cell.id);
        const transformedData: ITransformedData = {};
        upperSectionDataKeys.forEach(key => {
            const transformedKey = transformKey(key);
            transformedData[transformedKey] = {
                link: links && links[key] ? links[key] : undefined,
                value: data[key] || '',
                label: headCells.find(cell => cell.id === key)?.label || ''
            };
        });
        return transformedData;
    }, [data, headCells, links, topRowLength]);

    const transformedSubRowData = useMemo<ITransformedData>(() => {
        const subRowDataKeys = headCells.slice(topRowLength).map(cell => cell.id);
        const transformedData: ITransformedData = {};
        subRowDataKeys.forEach(key => {
            const transformedKey = transformKey(key);
            transformedData[transformedKey] = {
                link: links && links[key] ? links[key] : undefined,
                value: data[key] || '',
                label: headCells.find(cell => cell.id === key)?.label || '',
            };
        });
        return transformedData;
    }, [data, headCells, links, topRowLength]);


    const handleOpen = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        event.stopPropagation();
        if (event.currentTarget instanceof HTMLButtonElement) {
            setMenuAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setMenuAnchorEl(null);
    };

    const visibleButtonHandler = (e: any) => {
        e.stopPropagation();
        handleClose();
        visibleButton?.onClick(data);
    }

    return (
        <MainContainerItem sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: collapse ? 'flex-start' : "center"
        }} item className={rootClassName}>
            <Box sx={{
                cursor: "pointer",
                width: "100%",
            }} onClick={() => {
                if (handleToggle){
                    handleToggle(data.id as string)
                } else {
                    alert("Please provide handleToggle function")
                }
            }}>
                <Header container>
                    {Object.entries(transformedUpperSectionData).map(([key, { value, label, link }]) => (
                        <Grid sx={(theme) => ({
                            [theme.breakpoints.up('sm')]: {
                                width: 'auto',
                                maxWidth: '15.666667%',
                            }
                        })} xs={6} sm={4} md={2} item key={key}>
                            <InfoLabel variant="body2" className={labelClassName}>
                                {label || '-'}
                            </InfoLabel>
                            {
                                link ? <InfoValueLink
                                    href={formatLink(link.baseUrl, value, link.queryParam)}
                                    target={'_blank'}
                                    variant={'h6'}
                                    underline={'hover'}
                                    className={valueClassName}
                                  >
                                    {value || '-'}
                                  </InfoValueLink> :
                                  <InfoValue
                                    variant="h6"
                                    className={valueClassName}
                                  >
                                      {value || '-'}
                                  </InfoValue>
                            }
                        </Grid>
                    ))}
                    {visibleButton ? <Button variant='contained' onClick={visibleButtonHandler}>{visibleButton.label}</Button> : ''}
                    <HeaderAction item>
                        <IconButton style={{ padding: "0em" }}>
                            {collapse ? (
                                <Remove fontSize="medium" color="primary" />
                            ) : (
                                <Add fontSize="medium" color="primary" />
                            )}
                        </IconButton>
                    </HeaderAction>
                </Header>
            </Box>
            {subRowData.length > 0 && (
                <InfoValueData
                    className={subRowClassName}
                    in={collapse}
                    timeout="auto"
                    mountOnEnter
                    unmountOnExit={false}
                >
                    <SubRowHeader container>
                        {Object.entries(transformedSubRowData).map(([key, { value, label, link }]) => (
                            <Grid sx={(theme) => ({
                                [theme.breakpoints.up('sm')]: {
                                    width: 'auto',
                                    maxWidth: '15.666667%',
                                }
                            })} mb={3} xs={6} sm={4} md={2} item key={key}>
                                <InfoLabel variant="body2" className={labelClassName}>
                                    {label || '-'}
                                </InfoLabel>
                                {
                                    link ? <InfoValueLink
                                      href={formatLink(link.baseUrl, value, link.queryParam)}
                                      target={'_blank'}
                                      variant={'h6'}
                                      underline={'hover'}
                                      className={valueClassName}
                                      >
                                          {value || '-'}
                                      </InfoValueLink> :
                                      <InfoValue
                                      variant="h6"
                                      className={valueClassName}
                                    >
                                        {value || '-'}
                                    </InfoValue>
                                }
                            </Grid>
                        ))}
                        <HeaderActionSub
                            container
                            justifyContent="flex-end"
                            alignItems="center">
                            {
                                hasAction && <IconButton onClick={handleOpen}>
                                    <MoreHoriz fontSize="medium" color="primary" />
                                </IconButton>
                            }
                            {hasAction && actionItems && actionItems?.length > 0 && (
                                <MenuContainer
                                    id="simple-menu"
                                    anchorEl={menuAnchorEl}
                                    keepMounted
                                    open={Boolean(menuAnchorEl)}
                                    onClose={handleClose}
                                >
                                    {actionItems?.map((item, index) => (
                                        <MenuItem
                                            key={index}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClose();
                                                item.onClick(data);
                                            }}
                                        >
                                            {item.icon}
                                            <MarginLeftTypography
                                                variant="body1"
                                                color="primary"
                                            >
                                                {item.label}
                                            </MarginLeftTypography>
                                        </MenuItem>
                                    ))}
                                    {conditionalActionItems &&
                                        conditionalActionItems.find((item) => item.id === inx) && (
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClose();
                                                    conditionalActionItems.find((item) => item.id === inx)?.onClick(
                                                        data
                                                    );
                                                }}
                                            >
                                                {conditionalActionItems.find((item) => item.id === inx)?.icon}
                                                <MarginLeftTypography variant="body1" color="primary">
                                                    {conditionalActionItems.find((item) => item.id === inx)?.label}
                                                </MarginLeftTypography>
                                            </MenuItem>
                                        )}
                                </MenuContainer>
                            )}
                        </HeaderActionSub>
                    </SubRowHeader>
                </InfoValueData>
            )}
        </MainContainerItem>
    );
};

export default InfoCard;
